import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2, RotateCcw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isActiveJob, useAiVideoJobs, useDismissAiVideoJob, type AiVideoJob } from "@/hooks/use-ai-video";
import type { GenerateVideoInitial } from "./generate-video-dialog";

interface AiVideoJobsProps {
  brandId: string;
  /** Reopen the generate dialog prefilled with a failed draft's settings. */
  onRetry: (initial: GenerateVideoInitial) => void;
}

/** "Generating…" and failed cards shown above the Library grid. */
export function AiVideoJobs({ brandId, onRetry }: AiVideoJobsProps) {
  const { t } = useTranslation("pieces");
  const jobs = useAiVideoJobs(brandId).data ?? [];
  const dismiss = useDismissAiVideoJob(brandId);
  if (jobs.length === 0) return null;

  const describe = (j: AiVideoJob) =>
    j.type === "video_upscale"
      ? t("library.aiVideo.jobs.upscaling")
      : t("library.aiVideo.jobs.drafting", {
          details: [j.model, j.durationSeconds ? `${j.durationSeconds}s` : null, j.resolution, j.aspectRatio]
            .filter(Boolean)
            .join(" · "),
        });

  return (
    <section aria-label={t("library.aiVideo.jobs.title")} className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {jobs.map((j) => {
        const active = isActiveJob(j);
        return (
          <div
            key={j.id}
            className={`flex gap-3 rounded-lg border p-3 text-sm ${
              active ? "bg-muted/40" : "border-destructive/40 bg-destructive/5"
            }`}
          >
            {active ? (
              <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary" aria-hidden />
            ) : (
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
            )}
            <div className="min-w-0 flex-1 space-y-1">
              <div className="font-medium">
                {active ? describe(j) : t("library.aiVideo.jobs.failed")}
              </div>
              {j.prompt && (
                <p className="line-clamp-2 text-xs text-muted-foreground" title={j.prompt}>
                  {j.prompt}
                </p>
              )}
              {active ? (
                <p className="text-xs text-muted-foreground">{t("library.aiVideo.jobs.eta")}</p>
              ) : (
                <>
                  {j.error && <p className="text-xs text-destructive">{j.error}</p>}
                  <div className="flex gap-2 pt-1">
                    {j.type === "video_draft" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          onRetry({
                            prompt: j.prompt,
                            model: j.model,
                            aspectRatio: j.aspectRatio,
                            durationSeconds: j.durationSeconds,
                            resolution: j.resolution,
                            referenceAssetIds: j.referenceAssetIds,
                          })
                        }
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        {t("library.aiVideo.tryAgain")}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => dismiss.mutate(j.id)}>
                      <X className="h-3.5 w-3.5" />
                      {t("library.aiVideo.jobs.dismiss")}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
}
